{
  "$id": "https://ophelos/config/theme.schema.json",
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "ThemeConfig",
  "description": "UI theming configuration for colors, typography, logos, and layout",
  "type": "object",
  "additionalProperties": false,
  "properties": {
    "theme": {
      "type": "string",
      "enum": ["professional", "approachable"],
      "default": "professional",
      "description": "Theme preset style"
    },
    "showGradientSphere": {
      "type": "boolean",
      "default": false,
      "description": "Show gradient sphere background effect (default: true for approachable theme, false for professional)"
    },
    "defaultLanguage": {
      "type": "string",
      "enum": ["en", "pt", "fr", "cs", "da", "nl", "fi", "de", "el", "hu", "it", "nb", "sk", "es", "sv"],
      "default": "en",
      "description": "Default language for the journey"
    },
    "availableLanguages": {
      "type": "array",
      "items": {
        "type": "string",
        "enum": ["en", "pt", "fr", "cs", "da", "nl", "fi", "de", "el", "hu", "it", "nb", "sk", "es", "sv"]
      },
      "default": ["en"],
      "description": "Available languages for language selector"
    },
    "features": {
      "type": "object",
      "description": "UI feature toggles for the customer portal",
      "additionalProperties": false,
      "properties": {
        "enableChat": {
          "type": "boolean",
          "default": false,
          "description": "Enable chat functionality in the customer portal"
        },
        "showDisputes": {
          "type": "boolean",
          "default": true,
          "description": "Show disputes section in the customer portal"
        },
        "showDocuments": {
          "type": "boolean",
          "default": true,
          "description": "Show documents section in the customer portal"
        },
        "showAccountBalance": {
          "type": "boolean",
          "default": true,
          "description": "Show account balance in the customer portal"
        },
        "showPaymentHistory": {
          "type": "boolean",
          "default": true,
          "description": "Show payment history in the customer portal"
        }
      }
    },
    "logo_full": {
      "type": "object",
      "description": "Full logo URLs for light and dark modes",
      "additionalProperties": false,
      "properties": {
        "light": {
          "type": "string",
          "format": "uri",
          "description": "Full logo URL for light mode"
        },
        "dark": {
          "type": "string",
          "format": "uri",
          "description": "Full logo URL for dark mode"
        }
      },
      "required": ["light", "dark"]
    },
    "logo_icon": {
      "type": "object",
      "description": "Icon logo URLs for light and dark modes",
      "additionalProperties": false,
      "properties": {
        "light": {
          "type": "string",
          "format": "uri",
          "description": "Icon logo URL for light mode"
        },
        "dark": {
          "type": "string",
          "format": "uri",
          "description": "Icon logo URL for dark mode"
        }
      },
      "required": ["light", "dark"]
    },
    "favicon": {
      "type": "object",
      "description": "Favicon URLs for light and dark modes",
      "additionalProperties": false,
      "properties": {
        "light": {
          "type": "string",
          "format": "uri",
          "description": "Favicon URL for light mode"
        },
        "dark": {
          "type": "string",
          "format": "uri",
          "description": "Favicon URL for dark mode"
        }
      },
      "required": ["light", "dark"]
    },
    "colors": {
      "type": "object",
      "description": "Color palette configuration with light/dark mode support",
      "additionalProperties": false,
      "properties": {
        "brand": {
          "type": "string",
          "pattern": "^#[0-9a-fA-F]{6}$",
          "default": "#7f53dd",
          "description": "Main brand color (hex format, e.g., #7f53dd)"
        },
        "buttonBackground": {
          "type": "object",
          "description": "Button background colors for light and dark modes",
          "additionalProperties": false,
          "properties": {
            "light": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#7f53dd",
              "description": "Button background color in light mode"
            },
            "dark": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#7f53dd",
              "description": "Button background color in dark mode"
            }
          },
          "required": ["light", "dark"]
        },
        "buttonText": {
          "type": "object",
          "description": "Button text colors for light and dark modes",
          "additionalProperties": false,
          "properties": {
            "light": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#FFFFFF",
              "description": "Button text color in light mode"
            },
            "dark": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#FFFFFF",
              "description": "Button text color in dark mode"
            }
          },
          "required": ["light", "dark"]
        },
        "linksColor": {
          "type": "object",
          "description": "Link colors for emails (only light mode is used)",
          "additionalProperties": false,
          "properties": {
            "light": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#7f53dd",
              "description": "Link color in light mode"
            }
          },
          "required": ["light"]
        },
        "emailBackground": {
          "type": "object",
          "description": "Email background color (only light mode is used)",
          "additionalProperties": false,
          "properties": {
            "light": {
              "type": "string",
              "pattern": "^#[0-9a-fA-F]{6}$",
              "default": "#F6F9FC",
              "description": "Email background color in light mode"
            }
          },
          "required": ["light"]
        },
        "text": {
          "type": "string",
          "pattern": "^#[0-9a-fA-F]{6}$",
          "default": "#333333",
          "description": "Default text color (hex format)"
        },
        "muted": {
          "type": "string",
          "pattern": "^#[0-9a-fA-F]{6}$",
          "default": "#F3F4F6",
          "description": "Muted background color (hex format)"
        },
        "border": {
          "type": "string",
          "pattern": "^#[0-9a-fA-F]{6}$",
          "default": "#E5E7EB",
          "description": "Border color (hex format)"
        },
        "accent": {
          "type": "string",
          "pattern": "^#[0-9a-fA-F]{6}$",
          "default": "#F1E8FA",
          "description": "Accent color for highlights (hex format)"
        }
      }
    },
    "layout": {
      "type": "object",
      "description": "Layout and spacing configuration",
      "additionalProperties": false,
      "properties": {
        "borderRadius": {
          "type": "string",
          "enum": ["none", "small", "medium", "large"],
          "default": "medium",
          "description": "Border radius preset for UI components"
        }
      }
    }
  }
}
